/**
 * Background conversion of maps to schema v2
 * (docs/plans/2026-10-02-crdt-schema-v2.md). A map moves to v2 when someone
 * who can edit opens it (map-format-migration.js); maps nobody opens would
 * stay in the old format for ever, and the code reading it with them. So,
 * once per session, after the open map is synced and the browser is idle,
 * every map stored online that this user can edit and that the server lists
 * in an old format (or not reported yet: GET /api/docs/formats) is opened on
 * its own sync channel, converted (backup first, one transaction), and
 * closed once the server confirmed it. One map at a time; never the open
 * map. Each converted map keeps a copy on this device, as an opened one
 * does. Maps kept on this device only are converted when opened.
 * Alex, 2026-10-03: "convert all".
 *
 * The same pass drops a converted map's old copy two weeks after its
 * conversion (old-copy-drop.js, docs/plans/2026-10-13-drop-schema-v1.md):
 * maps the server lists as still holding it (format 1 or 2) unless this
 * device noted that the copy is not due yet, and maps whose compaction
 * after a drop is not confirmed.
 */
import { YjsDocManager } from "./yjs-doc.js";
import { SyncManager } from "./websocket.js";
import { unsyncedState, whenConfirmed } from "./unsynced-changes.js";
import { storageOf } from "../core/vault-folders.js";
import { on, Events } from "../core/events.js";
import { saveFormatBackup, dropFormatBackup } from "./map-format-migration.js";
import { dropOldCopyIfDue, compactAfterDrop, oldCopyDueDates } from "./old-copy-drop.js";
import { listOldCopyBackups, pruneOldCopyBackups } from "./migration-backups.js";
import { REPORTED_V2_WITHOUT_OLD_COPY } from "./map-format.js";

// Bounds (liveness, not timing): how long a map may take to sync, and its
// conversion to be confirmed, before it is left for the next session.
const SYNC_BOUND_MS = 60000;
const CONFIRM_BOUND_MS = 120000;

// One run per signed-in session: a sign-out ends it (the next sign-in may start another).
let started = false;
let run = 0;
const stopRun = () => { run++; started = false; };
on(Events.AUTH_LOGOUT, stopRun);
on(Events.SESSION_ENDED, stopRun);

/** The browser is idle (or soon: the bound keeps a busy page from never converting). */
const idle = () => new Promise((resolve) => {
  if (typeof globalThis.requestIdleCallback === "function") requestIdleCallback(() => resolve(), { timeout: 10000 });
  else resolve();
});

const within = (promise, ms) => Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(false), ms))]);

/**
 * Start the conversion for this session (no-op after the first call).
 * @param {{vault: Object, getCurrentDocId: () => string|null, getUserId: () => string|null}} cb
 */
export function scheduleBackgroundConversion(cb) {
  if (started || !cb?.vault) return;
  started = true;
  const mine = ++run;
  const stopped = () => mine !== run;
  void idle().then(() => convertAll(cb, stopped)).catch((err) => {
    console.warn("[BackgroundConversion] Stopped:", err);
  });
}

async function convertAll(cb, stopped) {
  const response = await fetch("/api/docs/formats", { credentials: "include" });
  if (!response.ok) {
    console.warn("[BackgroundConversion] Could not list map formats:", response.status);
    return { converted: 0 };
  }
  const { docs = [] } = await response.json();
  await pruneOldCopyBackups();
  const backups = await listOldCopyBackups();
  const due = oldCopyDueDates();
  const now = Date.now();
  const vaultDocs = new Map((cb.vault.getIndex?.()?.docs || []).map((doc) => [doc.docId, doc]));
  // A drop whose compaction this device has not seen confirmed.
  const owed = (docId) => backups[docId]?.compacted === false;
  const todo = docs.filter((doc) => doc.role !== "viewer"
    && vaultDocs.has(doc.docId) && storageOf(vaultDocs.get(doc.docId)) === "online"
    && (owed(doc.docId) || (doc.schema !== REPORTED_V2_WITHOUT_OLD_COPY && !(due[doc.docId] > now))));
  if (todo.length) console.log(`[BackgroundConversion] ${todo.length} map(s) to check for the old format`);
  const done = { converted: 0, dropped: 0 };
  for (const { docId } of todo) {
    if (stopped()) break;
    await idle();
    if (stopped()) break;
    if (docId === cb.getCurrentDocId()) continue; // the open path converts it
    try {
      const outcome = await convertOne(docId, cb, stopped, { owed: owed(docId) });
      if (outcome in done) done[outcome]++;
    } catch (err) {
      console.warn("[BackgroundConversion] Map left for later:", docId, err);
    }
  }
  if (done.converted) console.log(`[BackgroundConversion] ${done.converted} map(s) moved to schema v2`);
  if (done.dropped) console.log(`[BackgroundConversion] ${done.dropped} map(s) without their old copy`);
  return done;
}

/**
 * Open one map on its own channel, convert it or drop its old copy when
 * due, wait for the server.
 * @param {{owed: boolean}} options - owed: its compaction after a drop is not confirmed
 * @returns {Promise<'converted'|'dropped'|'compacted'|'already'|'skipped'>}
 */
async function convertOne(docId, cb, stopped, { owed = false } = {}) {
  const rawKey = await cb.vault.getDocDEK(docId);
  let key;
  try {
    key = await crypto.subtle.importKey("raw", rawKey, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
  } finally {
    rawKey.fill?.(0);
  }
  const manager = new YjsDocManager(docId, { userId: cb.getUserId() });
  let channel = null;
  let outcome = "skipped";
  try {
    await manager.initPersistence(); // this device's copy, unsent changes included
    // Anything unusual ends this map's turn; it is tried again next session.
    let interrupted = false;
    const stop = () => { interrupted = true; };
    channel = new SyncManager({
      isReadOnly: () => false,
      maxAttemptsWithoutSuccess: 1,
      onAccessDenied: stop,
      onChangesRefused: stop,
      onSessionInvalidated: stop,
      onDekRotated: stop,
      onUnshared: stop,
      onKeyOutOfSync: async () => { stop(); return null; },
      onError: (error) => console.warn("[BackgroundConversion] Sync error on", docId, error),
    });
    channel.setEncryptionKey(key);
    await channel.connect(docId, manager);
    if (!(await channel.waitForSync(SYNC_BOUND_MS)) || interrupted || stopped()) return outcome;
    if (manager.isV2()) return await dropOne(docId, manager, channel, cb, stopped, owed);
    if (docId === cb.getCurrentDocId() || !manager.hasData()) return outcome;
    await saveFormatBackup(docId, manager.createBackup());
    const result = manager.migrateToV2();
    if (!result.migrated) return outcome;
    outcome = "converted";
    // Confirmed: the server stored the conversion (until then it waits on
    // this device and is sent when the map is next opened or converted).
    if (await within(whenConfirmed(docId).then(() => true), CONFIRM_BOUND_MS) && unsyncedState(docId) === "clean") {
      await dropFormatBackup(docId);
    }
    console.log(`[BackgroundConversion] Map ${docId} moved to schema v2 (${result.count} nodes)`);
    await dropOne(docId, manager, channel, cb, stopped, false); // notes when its copy is due
    return outcome;
  } finally {
    try { await channel?.disconnect(); } catch (err) { console.warn("[BackgroundConversion] Disconnect failed:", err); }
    try { await manager.destroy(); } catch (err) { console.warn("[BackgroundConversion] Close failed:", err); }
  }
}

/**
 * A map in schema v2: drop its old copy if due, then compact it; or only
 * compact it, if a drop's compaction is owed.
 * @returns {Promise<'dropped'|'compacted'|'already'|'skipped'>}
 */
async function dropOne(docId, manager, channel, cb, stopped, owed) {
  const stillOpen = () => !stopped() && docId !== cb.getCurrentDocId();
  if (!stillOpen()) return "skipped";
  const result = await dropOldCopyIfDue(docId, manager, { stillOpen });
  if (!result.dropped && !owed) return "already";
  if (result.dropped) channel.reportFormat();
  const compacted = await compactAfterDrop(docId, () => channel.forceCompaction("old-copy-dropped"));
  if (result.dropped) {
    console.log(`[BackgroundConversion] Map ${docId}: old copy dropped (${result.count} entries)${compacted ? ", compacted" : ""}`);
    return "dropped";
  }
  return compacted ? "compacted" : "skipped";
}
